import { SkeletonBox } from '@apeiron/temp'
import { MuiLoading } from '@apeiron/ui'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import ImageBackground from '@public/images/background-battle-demo.jpg'
import ImageBottomLeft from '@public/images/image-battle-demo-left-bottom.png'
import ImageTopLeft from '@public/images/image-battle-demo-left-top.png'
import ImageMobileBottomLeft from '@public/images/image-battle-demo-mobile-left-bottom.png'
import ImageMobileTopLeft from '@public/images/image-battle-demo-mobile-left-top.png'
import ImageMobileBottomRight from '@public/images/image-battle-demo-mobile-right-bottom.png'
import ImageMobileTopRight from '@public/images/image-battle-demo-mobile-right-top.png'
import ImageBottomRight from '@public/images/image-battle-demo-right-bottom.png'
import ImageTopRight from '@public/images/image-battle-demo-right-top.png'
import ApeironCorneredBox from '@src/components/share/apeiron/ApeironCorneredBox'
import ApeironTabs, {
  ApeironTabsInput
} from '@src/components/share/apeiron/ApeironTabs'
import { ROUTE } from '@src/constants/route'
import theme from '@src/styles/theme'
import * as R from 'ramda'
import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(ApeironCorneredBox)`
  height: calc(100vh - 116px);
  width: 100%;
  ${props => props.theme.breakpoints.up('res768')} {
    background-image: url(${ImageBackground.src});
    background-size: cover;
    background-position: center;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    background: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)),
      url(${ImageBackground.src});
    background-size: cover;
    background-position: center bottom 60%;
  }

  .Custom-Container {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
  .Custom-Corner-Container {
    transform: rotate(180deg);
  }
  .Custom-Top-Left {
    aspect-ratio: 0.52;
    max-width: 150px;
    width: 100%;
  }
  .Custom-Bottom-Left {
    aspect-ratio: 0.545;
    max-width: 350px;
    width: 100%;
  }
  .Custom-Top-Right {
    aspect-ratio: 0.545;
    max-width: 120px;
    width: 100%;
  }
  .Custom-Bottom-Right {
    aspect-ratio: 1.755;
    bottom: -55px;
    max-width: 1000px;
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    .Custom-Top-Left {
      aspect-ratio: 0.883;
      max-width: 218px;
      width: 100%;
    }
    .Custom-Bottom-Left {
      aspect-ratio: 0.382;
      max-width: 42px;
      width: 100%;
    }
    .Custom-Top-Right {
      aspect-ratio: 0.765;
      max-width: 85px;
      width: 100%;
    }
    .Custom-Bottom-Right {
      aspect-ratio: 0.742;
      max-width: 319px;
      width: 100%;
    }
  }
`

const TabText = styled(Typography)`
  color: white;
  font-size: 16px;
  line-height: 16px;
`

const StyledApeironTabs = styled(ApeironTabs)`
  .Custom-Tabs-Section {
    max-width: 400px;
    margin: 0 auto;

    .Custom-Tabs-Container {
      .Custom-Tabs {
        .MuiTabs-indicator {
          background: #2d91ec;
          width: 100%;
          height: 5px;
        }

        ${props => props.theme.breakpoints.up('res768')} {
          border-radius: unset;
          .MuiTab-root {
            background-color: rgba(45, 47, 48, 1);
            margin: 0;
            height: 60px;
            width: 200px;
          }
          .Mui-selected {
            background: linear-gradient(180deg, #2d3846 0%, #2d5386 100%);
          }
        }
      }
    }
  }
  gap: 0;
`

const ContentContainer = styled(SkeletonBox)`
  display: flex;
  flex: 1;
  justify-content: center;
  margin: 40px 0px 0px 0px;
  width: 100%;
`

const PlaceHolderBox = styled(Box)`
  display: flex;
  align-items: center;
  background-color: #141415dd;
  height: 70vh;
  justify-content: center;
  max-height: 640px;
  max-width: 1024px;
  width: 98vw;
`

const ApostleAttachReforgeTabs: FC<Props> = (props: Props) => {
  const { className, children, navbarIndex } = props

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res768'))

  const Loading = (
    <PlaceHolderBox>
      <MuiLoading loading />
    </PlaceHolderBox>
  )

  const tabsInput = useMemo(() => {
    const tabs: ApeironTabsInput[] = [
      {
        label: <TabText>{t('sanctuary.manage_apostle.tab.attach')}</TabText>,
        value: '0',
        url: ROUTE.SANCTUARY_CELESTIAL_BINDING,
        panel: Loading
      },
      {
        label: <TabText>{t('sanctuary.manage_apostle.tab.reforge')}</TabText>,
        value: '1',
        url: ROUTE.SANCTUARY_REFORGE_APOSTLE,
        panel: Loading
      }
    ]

    return R.map((tab: ApeironTabsInput) => {
      return tab.value === navbarIndex ? R.assoc('panel', children, tab) : tab
    }, tabs)
  }, [t, navbarIndex, children])

  return (
    <>
      {mobile ? (
        <ContentContainer>
          <StyledApeironTabs input={tabsInput} index={navbarIndex} />
        </ContentContainer>
      ) : (
        <Container
          className={className}
          bottomLeft={mobile ? ImageMobileBottomLeft.src : ImageBottomLeft.src}
          bottomRight={
            mobile ? ImageMobileBottomRight.src : ImageBottomRight.src
          }
          topLeft={mobile ? ImageMobileTopLeft.src : ImageTopLeft.src}
          topRight={mobile ? ImageMobileTopRight.src : ImageTopRight.src}
        >
          <ContentContainer>
            <StyledApeironTabs input={tabsInput} index={navbarIndex} />
          </ContentContainer>
        </Container>
      )}
    </>
  )
}

type Props = {
  className?: string
  children?: ReactNode
  navbarIndex: string
}

export default ApostleAttachReforgeTabs
